import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { ObservableArray, Subscribable, SubscribableOrNullableValue } from 'knockout';
import { CurrentUserManager } from 'managers/currentUser';
import { IWCCStorageManager } from 'managers/iStorage';
import { TopicManager } from 'managers/topic';
import { EffectsContainer } from 'mixins/withEffects';
import WCCError from 'models/wccError';
import { TopicsService } from 'services/topics';
import TaskActivityIdeationToolsStageButton from './stageButton';
import './tools.html';
import './tools.scss';

export interface TaskActivityIdeationToolsVMConfig {
    topicId: SubscribableOrNullableValue<string>
}

@injectable()
export default class TaskActivitiyIdeationToolsVM {
    private topicId: Subscribable<string | undefined>
    private activeStage: Subscribable<number>

    isReadOnly: Subscribable<boolean>

    stageButtons: ObservableArray<TaskActivityIdeationToolsStageButton>

    constructor(
        @inject(wccModules.componentConfig) config: TaskActivityIdeationToolsVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.topicsService) private topicsService: TopicsService,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        const userManager = storage.get(CurrentUserManager, { discussionId: settings.discussionId });
        const user = userManager.pluck(m => m.person);
        const isModerator = user.pluck(u => u.isModerator, false);

        const topicManager = storage.get(TopicManager, { topicId: config.topicId });
        const topic = topicManager.pluck(m => m.topic);
        this.topicId = topic.pluck(t => t.id);
        this.activeStage = topic.pluck(t => t.ideationStage, 0);

        this.stageButtons = ko.observableArray([
            new TaskActivityIdeationToolsStageButton({ stage: enums.IdeationStages.IdeaCreation.value }),
            new TaskActivityIdeationToolsStageButton({ stage: enums.IdeationStages.IdeaReview.value }),
            new TaskActivityIdeationToolsStageButton({ stage: enums.IdeationStages.IdeaRating.value })
        ]);

        this.isReadOnly = isModerator.not();

        const isReady = ko.strictComputed((user, topic) => {
            return user != undefined &&
                topic != undefined &&
                topic.postCreatorRestrictionType() != enums.PostCreatorRestrictionTypes.Moderator.value;
        }, [user, topic]);

        effects.register(stage => {
            this.activateStageButton(stage);
        }, [this.activeStage]);

        ko.applyBindingsToNode(info.element, {
            'visible': isReady,

            'css': {
                'task-activity-ideation-tools': true
            }
        });
    }

    onStageButtonClick = (stageButton: TaskActivityIdeationToolsStageButton) => {
        if (!this.isReadOnly() && !stageButton.isActive()) {
            system.confirm(`Are you sure you want to activate '${stageButton.title()}' stage?`, async () => {
                try {
                    await this.activateStage(stageButton.stage());
                } catch (ex) {
                    system.handleError(ex);
                }
            });            
        }
    }

    private async activateStage(stage: number) {
        const topicId = this.topicId();

        if (topicId == undefined)
            throw new WCCError(messages.UnknownError);

        await this.topicsService.setIdeationStage(topicId, stage);
    }

    private activateStageButton(stage: number) {
        this.stageButtons().forEach(b => b.isActive(b.stage() === stage));
    }
}